export const PRE_SALES_IMPORT_SCHEMA_VERSION = "1.0.0";

export const CONFIGURATION_ROLES = [
  "first_contact_attempt",
  "first_contact_confirmed",
  "second_contact",
  "third_contact",
  "additional_contacts",
  "customer_replied",
  "qualification_started",
  "qualified",
  "disqualified",
  "handoff_started",
  "handoff_accepted",
  "handoff_rejected",
  "won",
  "lost",
  "archived",
  "ignored",
];

export const EMPTY_CONFIGURATION = {
  processes: [],
  cadence: { minimum_attempts: 3 },
  temperature_rules: { cold: 1, warm: 2, hot: 3 },
  slas: { first_contact_hours: 4, handoff_hours: 24 },
  goals: {},
  large_project: { threshold: null },
  forecast: {
    cold_probability: 0.2,
    warm_probability: 0.5,
    hot_probability: 0.8,
    unclassified_probability: 0,
    minimum_sample_size: 30,
    horizon_days: 90,
    history_window_days: 365,
  },
  origin_rules: { require_explicit_alias: true },
  campaign_attribution: {},
  communication_channels: {},
};

const CONFIGURATION_KEYS = new Set(Object.keys(EMPTY_CONFIGURATION));
const TERMINAL_ROLES = new Set(["won", "lost", "archived"]);

const clone = (value) => JSON.parse(JSON.stringify(value));

const uniquePositiveIntegers = (value) =>
  Array.from(
    new Set(
      (Array.isArray(value) ? value : [])
        .map(Number)
        .filter((item) => Number.isInteger(item) && item > 0),
    ),
  );

export const normalizeConfiguration = (value) => {
  const source = value && typeof value === "object" ? value : {};
  return {
    ...clone(EMPTY_CONFIGURATION),
    ...source,
    processes: Array.isArray(source.processes)
      ? source.processes.map((process) => ({
          process_id:
            process?.process_id === null
              ? null
              : Number.isInteger(Number(process?.process_id))
                ? Number(process.process_id)
                : process?.process_id,
          roles:
            process?.roles && typeof process.roles === "object"
              ? Object.fromEntries(
                  Object.entries(process.roles).map(([role, ids]) => [
                    role,
                    uniquePositiveIntegers(ids),
                  ]),
                )
              : {},
        }))
      : [],
    cadence: {
      ...EMPTY_CONFIGURATION.cadence,
      ...(source.cadence || {}),
    },
    temperature_rules: clone(EMPTY_CONFIGURATION.temperature_rules),
    slas: { ...EMPTY_CONFIGURATION.slas, ...(source.slas || {}) },
    goals: { ...EMPTY_CONFIGURATION.goals, ...(source.goals || {}) },
    large_project: {
      ...EMPTY_CONFIGURATION.large_project,
      ...(source.large_project || {}),
    },
    forecast: {
      ...EMPTY_CONFIGURATION.forecast,
      ...(source.forecast || {}),
    },
    origin_rules: {
      ...EMPTY_CONFIGURATION.origin_rules,
      ...(source.origin_rules || {}),
    },
    campaign_attribution: {
      ...EMPTY_CONFIGURATION.campaign_attribution,
      ...(source.campaign_attribution || {}),
    },
    communication_channels: {
      ...EMPTY_CONFIGURATION.communication_channels,
      ...(source.communication_channels || {}),
    },
  };
};

export const createConfigurationPackage = ({
  configuration,
  justification = "",
  effectiveFrom = "",
  retroactiveAuthorized = false,
}) =>
  JSON.stringify(
    {
      schema_version: PRE_SALES_IMPORT_SCHEMA_VERSION,
      configuration: normalizeConfiguration(configuration),
      justification,
      effective_from: effectiveFrom || null,
      retroactive_authorized: Boolean(retroactiveAuthorized),
    },
    null,
    2,
  );

export const validateConfigurationImport = (text, catalog = {}) => {
  const errors = [];
  const warnings = [];
  let parsed;

  try {
    parsed = JSON.parse(String(text || ""));
  } catch (error) {
    return {
      valid: false,
      errors: [`JSON inválido: ${error.message}`],
      warnings,
      configuration: null,
      metadata: {},
      summary: null,
    };
  }

  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    errors.push("O conteúdo importado precisa ser um objeto JSON.");
  }
  if ("empresa_id" in (parsed || {}) || "tenant_id" in (parsed || {})) {
    errors.push(
      "O JSON não pode escolher empresa ou tenant; o tenant vem exclusivamente do usuário autenticado.",
    );
  }

  const isPackage = Object.prototype.hasOwnProperty.call(
    parsed || {},
    "configuration",
  );
  const schemaVersion = isPackage
    ? String(parsed.schema_version || "")
    : PRE_SALES_IMPORT_SCHEMA_VERSION;
  if (
    isPackage &&
    schemaVersion !== PRE_SALES_IMPORT_SCHEMA_VERSION
  ) {
    errors.push(
      `Versão de importação incompatível: ${schemaVersion || "ausente"}. Esperada: ${PRE_SALES_IMPORT_SCHEMA_VERSION}.`,
    );
  }

  const rawConfiguration = isPackage ? parsed.configuration : parsed;
  if (
    !rawConfiguration ||
    typeof rawConfiguration !== "object" ||
    Array.isArray(rawConfiguration)
  ) {
    errors.push("O campo configuration precisa ser um objeto.");
  }
  if (
    "empresa_id" in (rawConfiguration || {}) ||
    "tenant_id" in (rawConfiguration || {})
  ) {
    errors.push(
      "A configuração não pode conter empresa_id ou tenant_id.",
    );
  }

  for (const key of Object.keys(rawConfiguration || {})) {
    if (!CONFIGURATION_KEYS.has(key)) {
      warnings.push(
        `O campo desconhecido "${key}" não será aplicado por esta versão.`,
      );
    }
  }

  const sanitized = Object.fromEntries(
    Object.entries(rawConfiguration || {}).filter(([key]) =>
      CONFIGURATION_KEYS.has(key),
    ),
  );
  const configuration = normalizeConfiguration(sanitized);
  const boards = new Map(
    (catalog.boards || []).map((board) => [Number(board.id), board]),
  );
  const columns = new Map(
    (catalog.columns || []).map((column) => [Number(column.id), column]),
  );
  const processIds = new Set();
  const mappedColumnIds = new Set();

  if (!configuration.processes.length) {
    errors.push("Selecione ao menos um processo.");
  }

  const importedTemperature = rawConfiguration?.temperature_rules;
  if (
    importedTemperature &&
    (Number(importedTemperature.cold) !== 1 ||
      Number(importedTemperature.warm) !== 2 ||
      Number(importedTemperature.hot) !== 3)
  ) {
    warnings.push(
      "temperature_rules foi normalizado para os códigos canônicos fixos: frio=1, morno=2 e quente=3.",
    );
  }

  const minimumAttempts = Number(configuration.cadence.minimum_attempts);
  if (!Number.isInteger(minimumAttempts) || minimumAttempts <= 0) {
    errors.push("cadence.minimum_attempts precisa ser um inteiro positivo.");
  }
  for (const [field, value] of Object.entries(configuration.slas)) {
    if (!Number.isFinite(Number(value)) || Number(value) <= 0) {
      errors.push(`slas.${field} precisa ser um número positivo.`);
    }
  }
  const threshold = configuration.large_project.threshold;
  if (threshold === null || threshold === undefined || threshold === "") {
    warnings.push(
      "large_project.threshold está vazio; o indicador de grande projeto ficará pendente.",
    );
  } else if (!Number.isFinite(Number(threshold)) || Number(threshold) <= 0) {
    errors.push("large_project.threshold precisa ser um número positivo.");
  }
  for (const field of [
    "cold_probability",
    "warm_probability",
    "hot_probability",
    "unclassified_probability",
  ]) {
    const probability = Number(configuration.forecast[field]);
    if (
      !Number.isFinite(probability) ||
      probability < 0 ||
      probability > 1
    ) {
      errors.push(`forecast.${field} precisa estar entre 0 e 1.`);
    }
  }
  for (const field of [
    "minimum_sample_size",
    "horizon_days",
    "history_window_days",
  ]) {
    const value = Number(configuration.forecast[field]);
    if (!Number.isInteger(value) || value <= 0) {
      errors.push(`forecast.${field} precisa ser um inteiro positivo.`);
    }
  }
  if (
    Number(configuration.forecast.history_window_days) <=
    Number(configuration.forecast.horizon_days)
  ) {
    errors.push(
      "forecast.history_window_days precisa ser maior que forecast.horizon_days.",
    );
  }

  for (const process of configuration.processes) {
    const processId = process.process_id;
    const processKey = processId === null ? "legacy" : String(processId);
    if (processIds.has(processKey)) {
      errors.push(`O processo ${processId ?? "legado"} está duplicado.`);
    }
    processIds.add(processKey);
    if (processId !== null && !boards.has(Number(processId))) {
      errors.push(
        `O processo ${processId} não existe no tenant autenticado.`,
      );
    }

    const rolesByColumn = new Map();
    for (const [role, ids] of Object.entries(process.roles || {})) {
      if (!CONFIGURATION_ROLES.includes(role)) {
        errors.push(`Papel de coluna desconhecido: ${role}.`);
        continue;
      }
      for (const columnId of uniquePositiveIntegers(ids)) {
        const column = columns.get(columnId);
        mappedColumnIds.add(columnId);
        if (!column) {
          errors.push(
            `A coluna ${columnId}, usada em ${role}, não existe no tenant autenticado.`,
          );
          continue;
        }
        const actualProcessId =
          column.board_id === null ? null : Number(column.board_id);
        if (actualProcessId !== processId) {
          errors.push(
            `A coluna #${columnId} pertence ao processo ${actualProcessId ?? "legado"}, não ao processo ${processId ?? "legado"}.`,
          );
        }
        if (!rolesByColumn.has(columnId)) rolesByColumn.set(columnId, []);
        rolesByColumn.get(columnId).push(role);
      }
    }

    for (const requiredRole of ["won", "lost"]) {
      if (!uniquePositiveIntegers(process.roles?.[requiredRole]).length) {
        errors.push(
          `O processo ${processId ?? "legado"} precisa mapear o papel ${requiredRole}.`,
        );
      }
    }
    for (const [columnId, roles] of rolesByColumn) {
      if (roles.includes("ignored") && roles.length > 1) {
        errors.push(
          `A coluna #${columnId} não pode ser ignorada e possuir outro papel.`,
        );
      }
      if (roles.filter((role) => TERMINAL_ROLES.has(role)).length > 1) {
        errors.push(
          `A coluna #${columnId} não pode representar mais de um resultado terminal.`,
        );
      }
    }
  }

  const metadata = isPackage
    ? {
        justification:
          typeof parsed.justification === "string"
            ? parsed.justification
            : "",
        effective_from:
          typeof parsed.effective_from === "string"
            ? parsed.effective_from
            : "",
        retroactive_authorized: Boolean(parsed.retroactive_authorized),
      }
    : {};

  return {
    valid: errors.length === 0,
    errors,
    warnings,
    configuration,
    metadata,
    summary: {
      processes: configuration.processes.length,
      mapped_columns: mappedColumnIds.size,
      mapped_roles: configuration.processes.reduce(
        (total, process) =>
          total +
          Object.values(process.roles || {}).filter(
            (ids) => Array.isArray(ids) && ids.length,
          ).length,
        0,
      ),
    },
  };
};
